/**
 * Generic open/close lifecycle for a popup listbox — Escape-to-close and
 * (optional) click-outside-to-close, the same pattern as
 * `PlayerCard.vue`'s `+ Tag` dropdown.
 *
 * Two refs are exposed: the trigger (the button that opens the listbox) and
 * the list root (the `<ul role="listbox">`). The composable attaches /
 * detaches a document-level `mousedown` listener WHILE the listbox is open
 * so a board with many `PlayerCard` instances doesn't accumulate one
 * always-on global listener per card.
 *
 * Listeners are added inside a Vue watcher so both:
 *
 *   - the open→closed transition (close cancels the listener), and
 *   - the view unmounting while the listbox is still open
 *
 * are routed through `onCleanup`, exactly mirroring the original
 * `PlayerCard` implementation. Without `onCleanup`, a stale
 * `mousedown`/`keydown` listener could outlive the destroyed component
 * and call `closeListbox()` against an unset ref.
 *
 * `onSelect` is wired through `select(value)` so the option-click handler
 * stays a one-liner: dispatch the mutation, then `select(value)`. The
 * composable also closes the listbox after a successful selection,
 * matching the existing `PlayerCard` behavior where picking a tag auto-
 * closes the dropdown. `select(value)` without an `onSelect` callback
 * still closes — the selection-result handoff is purely additive.
 */
import {
  onBeforeUnmount,
  ref,
  watch,
  type Ref,
} from 'vue'

export interface UseListboxOptions {
  /**
   * Optional selection callback. Fired by `select(value)` after the listbox
   * closes. Returns nothing — callers that need to read the result in the
   * parent component should use a Pinia store directly. The composable
   * closes the listbox regardless of whether `onSelect` is set, since the
   * common case is "mutate the store and dismiss the popup".
   */
  readonly onSelect?: (value: string) => void
  /**
   * When `true` (default), a `mousedown` outside both the trigger and the
   * list root closes the listbox. The trigger check is required so clicking
   * the trigger to toggle the listbox doesn't *also* fire the click-outside
   * handler and immediately close it again.
   */
  readonly closeOnOutsideClick?: boolean
}

export interface UseListboxReturn {
  /** Open / closed flag. Bind to a `v-if` or `v-show` on the list root. */
  readonly open: Ref<boolean>
  /** Ref to the trigger element (e.g. `<button>`). Wired so click-outside can ignore it. */
  readonly triggerRef: Ref<HTMLElement | null>
  /** Ref to the list root (`<ul role="listbox">`). */
  readonly listRef: Ref<HTMLElement | null>
  /** Open the listbox if currently closed. Caller wires the `@click` toggle. */
  readonly openListbox: () => void
  /** Close unconditionally. Idempotent. */
  readonly closeListbox: () => void
  /** Flip `open`. */
  readonly toggleListbox: () => void
  /** Wire to `@keydown` on the trigger / list. Closes on `Escape`. */
  readonly onKeydown: (e: KeyboardEvent) => void
  /** Wire to `@mousedown` on the document (added/removed dynamically with open state). */
  readonly onDocMouseDown: (e: MouseEvent) => void
  /**
   * Selection helper: fires `onSelect` (if defined) and closes the listbox.
   * Wire the `@click` on each option to `:on-click="() => select(option.id)"`.
   */
  readonly select: (value: string) => void
}

/**
 * Build the listbox lifecycle. Defaults to `closeOnOutsideClick: true` so
 * the common case (click outside to dismiss) needs no opt-in.
 */
export function useListbox(opts: UseListboxOptions = {}): UseListboxReturn {
  const { closeOnOutsideClick = true, onSelect } = opts

  const open = ref<boolean>(false)
  const triggerRef = ref<HTMLElement | null>(null)
  const listRef = ref<HTMLElement | null>(null)

  function openListbox(): void {
    open.value = true
  }

  function closeListbox(): void {
    open.value = false
  }

  function toggleListbox(): void {
    open.value = !open.value
  }

  function select(value: string): void {
    if (onSelect) onSelect(value)
    closeListbox()
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && open.value) {
      event.preventDefault()
      closeListbox()
    }
  }

  /**
   * Document-level mousedown. `mousedown` (not `click`) is intentional so
   * the listbox closes the moment the user begins to dismiss it, not after
   * a completing click — keeps the visual state consistent with intent.
   */
  function onDocMouseDown(event: MouseEvent): void {
    if (!open.value) return
    const target = event.target
    if (!(target instanceof Node)) return
    if (triggerRef.value && triggerRef.value.contains(target)) return
    if (listRef.value && listRef.value.contains(target)) return
    closeListbox()
  }

  // Attach / detach the document-level handlers only while the listbox is
  // open. `onCleanup` is invoked both when `open` flips (so a closed
  // listbox doesn't keep a stale listener) and on view unmount (so an
  // unmount with the listbox open doesn't leak a listener pointing at
  // a destroyed ref).
  if (closeOnOutsideClick) {
    watch(open, (isOpen, _prev, onCleanup) => {
      if (!isOpen) return
      document.addEventListener('mousedown', onDocMouseDown)
      document.addEventListener('keydown', onKeydown)
      onCleanup(() => {
        document.removeEventListener('mousedown', onDocMouseDown)
        document.removeEventListener('keydown', onKeydown)
      })
    })
  }

  // Belt + suspenders: even if the watcher somehow misses a cleanup (e.g.
  // a teardown path that sets `open = false` outside its current effect
  // scope), `onBeforeUnmount` drops whatever handlers might still be
  // attached.
  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onDocMouseDown)
    document.removeEventListener('keydown', onKeydown)
    open.value = false
  })

  return {
    open,
    triggerRef,
    listRef,
    openListbox,
    closeListbox,
    toggleListbox,
    onKeydown,
    onDocMouseDown,
    select,
  }
}
